import {Trans} from '@lingui/macro';
import React, {FC, useMemo, useState} from 'react';

import {Handler, HandlerOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {CustomError} from '../../../libs/shared/helpers/src/types/customErrors';
import {arrayToggle} from '../../../libs/shared/helpers/src/utilities/arrayHelpers';
import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {VisualSizesEnum} from '../../../libs/shared/ui/src/styles/commonStyles';
import {PaletteColorsEnum} from '../../../libs/shared/ui/src/styles/paletteModel';
import {useConstant} from '../../../srcCommon/helpers/hookHelpers';
import {SettingsComposerContainer} from '../../components/inboxes/composer/settings/settingsComposerContainer';
import {findInlineAttachmentsInHtml} from '../../helpers/models/messageHelpers';
import {isTeamNamespace} from '../../helpers/models/namespaceHelpers';
import {AnswerModel} from '../../models/api/answerModel';
import {UploadedAttachmentModel} from '../../models/api/attachmentModel';
import {InboxModel} from '../../models/api/inboxModel';
import {SettingsAnswersUrl} from '../../models/url/settingsUrlModel';
import {
  ComposerVariableTypesEnum,
  InterfaceEventsEnum,
  SettingsTrackingTypesEnum,
  SurveyTypesEnum,
  trackInterfaceEvent,
} from '../../services/tracking';
import {isStaticVariable} from '../../services/variableInterpolation/variableInterpolator';
import {AnswerNameAlreadyExistsError} from '../../store/data/answers/answerActions';
import {AnswerTemplate} from '../../store/data/answers/saveAnswerActions';
import {SettingsBannersEnum, useSettingsBanners} from '../banners/settingsBannersContext';
import {SettingsInboxesField} from '../common/settingsInboxesField';
import {
  SETTINGS_FOOTER_HEIGHT,
  SettingsFooter,
  StyledSectionedFieldsContainer,
  StyledSettingsFormContent,
  StyledSettingsFormField,
  StyledSettingsFormHeaderButtons,
  StyledSettingsFormMain,
  StyledSettingsFormTextBox,
  StyledSettingsWrapper,
} from '../common/settingsWrapper';
import {getSettingsType} from '../rules/ruleHelpers';
import {SettingsAnswersHeaderContainer} from './settingsAnswersHeaderContainer';
import {SettingsSingleAnswerParentFolderField} from './settingsSingleAnswerParentFolderField';

/*
 * Props.
 */

interface SettingsSingleAnswerProps {
  answersUrl: SettingsAnswersUrl;
  navigateToFolder: HandlerOf<number>;
  openRoot: Handler;
  answer?: AnswerModel;
  isEditing?: boolean;
  prefilledFolderId?: number;
  onDeleteClick: Handler;
  onSaveClick: (template: AnswerTemplate) => Promise<void | AnswerModel>;
  inboxes: ReadonlyArray<InboxModel>;
}

/*
 * Component.
 */

export const SettingsSingleAnswer: FC<SettingsSingleAnswerProps> = (props) => {
  const {
    answersUrl,
    navigateToFolder,
    openRoot,
    answer,
    isEditing,
    inboxes,
    onDeleteClick,
    prefilledFolderId,
  } = props;

  const initialAnswerState = useConstant(() => ({
    name: answer?.name || '',
    subject: answer?.subject || '',
    body: answer?.body || '',
    namespace: answer?.namespace || answersUrl.namespace,
    showInAllInboxes: answer?.showInAllInboxes ?? true,
    inboxIds: answer?.inboxIds ?? [],
    attachments: answer?.attachments ?? [],
    folderId: prefilledFolderId || answer?.folderId,
  }));

  const {namespace} = initialAnswerState;

  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isDirty, setIsDirty] = useState<boolean>(!answer);
  const [error, setError] = useState<CustomError | undefined>(undefined);
  const [name, setName] = useState<string>(initialAnswerState.name);
  const [subject, setSubject] = useState<string>(initialAnswerState.subject);
  const [body, setBody] = useState<string>(initialAnswerState.body);
  const [isBodyEmpty, setIsBodyEmpty] = useState<boolean>(initialAnswerState.body.trim().length === 0);
  const [attachments, setAttachments] = useState<ReadonlyArray<UploadedAttachmentModel>>(
    initialAnswerState.attachments,
  );
  const [showInAllInboxes, setShowInAllInboxes] = useState<boolean>(initialAnswerState.showInAllInboxes);
  const [inboxIds, setInboxIds] = useState(initialAnswerState.inboxIds);
  const [folderId, setFolderId] = useState(initialAnswerState.folderId);

  const {showBanner} = useSettingsBanners();

  const onCancelClick = () => {
    if (initialAnswerState.folderId) {
      props.navigateToFolder(initialAnswerState.folderId);
    } else {
      props.openRoot();
    }
  };

  const renderHeaderRight = () => (
    <StyledSettingsFormHeaderButtons>
      <Button
        size={VisualSizesEnum.MEDIUM}
        variant={ButtonVariantsEnum.SECONDARY_DANGER}
        color={PaletteColorsEnum.RED}
        onClick={onDeleteClick}
      >
        <Trans id="settings-deleteanswer">Delete template</Trans>
      </Button>
    </StyledSettingsFormHeaderButtons>
  );

  const canSave = useMemo(
    () => !isSaving && Boolean(name && namespace) && !isBodyEmpty,
    [isBodyEmpty, isSaving, name, namespace],
  );

  const onOkClick = async () => {
    try {
      setError(undefined);
      setIsSaving(true);
      setIsDirty(false);

      const attachmentCidsInBody = findInlineAttachmentsInHtml(body).map((a) => a.cid);

      const template: AnswerTemplate = {
        name,
        subject,
        body,
        namespace,
        showInAllInboxes,
        inboxIds,
        attachments: attachments.filter((a) => {
          if (a.isInline) {
            return attachmentCidsInBody.includes(a.cid);
          }

          return true;
        }),
        folderId,
      };

      await props.onSaveClick(template);

      trackInterfaceEvent(
        isEditing
          ? InterfaceEventsEnum.SETTINGS_CANNED_ANSWER_UPDATE
          : InterfaceEventsEnum.SETTINGS_CANNED_ANSWER_CREATE,
        {
          hasBodyChanged: isEditing ? body !== initialAnswerState.body : undefined,
          canned_answer_id: answer?.id,
          canned_answer_folder_id: folderId,
          if_nested_folder: Boolean(folderId),
          settings_type: getSettingsType(namespace),
        },
      );

      if (isEditing) {
        showBanner({
          type: SettingsBannersEnum.SUCCESS,
          message: (
            <Trans id="settingsanswers-update-success">
              Template &quot;{template.name}&quot; has been updated
            </Trans>
          ),
        });
      } else {
        showBanner({
          type: SettingsBannersEnum.SUCCESS,
          message: (
            <Trans id="settingsanswers-create-success">
              Template &quot;{template.name}&quot; has been created
            </Trans>
          ),
        });
      }

      if (template.folderId) {
        props.navigateToFolder(template.folderId);
      } else {
        props.openRoot();
      }
    } catch (caughtError) {
      setIsSaving(false);
      setIsDirty(true);
      if (caughtError instanceof CustomError) {
        setError(caughtError);
      } else {
        throw caughtError;
      }
    }
  };

  const maybeRenderFooter = () => {
    if (!isDirty) {
      return null;
    }

    return (
      <SettingsFooter>
        <Button variant={ButtonVariantsEnum.SECONDARY} onClick={onCancelClick}>
          <Trans id="settingstags-cancel">Cancel</Trans>
        </Button>
        <Button variant={ButtonVariantsEnum.PRIMARY} onClick={onOkClick} isDisabled={!canSave}>
          {isEditing ? (
            <Trans id="settingstags-savetag">Save</Trans>
          ) : (
            <Trans id="settingstags-createtag">Create</Trans>
          )}
        </Button>
      </SettingsFooter>
    );
  };

  const toolbarBottom = isDirty ? SETTINGS_FOOTER_HEIGHT : 0;

  return (
    <StyledSettingsWrapper>
      <SettingsAnswersHeaderContainer
        answersUrl={answersUrl}
        navigateToFolder={navigateToFolder}
        openRoot={openRoot}
        isEditing={isEditing}
        right={isEditing ? renderHeaderRight() : undefined}
      />
      <StyledSettingsFormMain>
        <StyledSettingsFormContent>
          <StyledSettingsFormField>
            <StyledSettingsFormTextBox
              $width={440}
              label={<Trans id="answereditor-name">Name</Trans>}
              value={name}
              onChange={(newName) => {
                setIsDirty(true);
                setName(newName);
              }}
              message={
                <Trans id="answereditor-namelabel">
                  Give your template a name so you can quickly find it later.
                </Trans>
              }
              isRequired
              shouldAutoFocus
              errorMessage={
                error instanceof AnswerNameAlreadyExistsError && (
                  <Trans id="answer-create-name-exist-error">This name is already used</Trans>
                )
              }
              hasError={error !== undefined}
            />
          </StyledSettingsFormField>
          <StyledSettingsFormField>
            <StyledSettingsFormTextBox
              $width={440}
              label={<Trans id="answereditor-subject">Subject</Trans>}
              message={
                <Trans id="answereditor-subjectlabel">
                  Your subject line will update when you use this template in an email.
                </Trans>
              }
              value={subject}
              onChange={(newSubject) => {
                setIsDirty(true);
                setSubject(newSubject);
              }}
              hasError={error !== undefined}
            />
          </StyledSettingsFormField>
          <SettingsComposerContainer
            attachments={attachments}
            onAddAttachment={(attachment) => {
              setAttachments((prev) => [...prev, attachment]);
              setIsDirty(true);
            }}
            onRemoveAttachment={(cid) => {
              setAttachments((prev) => prev.filter((a) => a.cid !== cid));
              setIsDirty(true);
            }}
            initialCleanHtml={initialAnswerState.body}
            onChange={(text, isEmpty) => {
              setIsBodyEmpty(isEmpty);
              setBody(text);
              setIsDirty(true);
            }}
            onSurveyAdded={() => {
              setIsDirty(true);
              trackInterfaceEvent(InterfaceEventsEnum.SETTINGS_CANNED_ANSWER_ADD_SURVEY, {
                settings_type: isTeamNamespace(namespace)
                  ? SettingsTrackingTypesEnum.TEAM
                  : SettingsTrackingTypesEnum.PERSONAL,
                survey_type: SurveyTypesEnum.CSAT_FIVE_STAR,
              });
            }}
            onVariableInserted={(variableName) => {
              trackInterfaceEvent(InterfaceEventsEnum.SETTINGS_CANNED_ANSWER_VARIABLE_MENU_SELECT, {
                settings_type: isTeamNamespace(namespace)
                  ? SettingsTrackingTypesEnum.TEAM
                  : SettingsTrackingTypesEnum.PERSONAL,
                variable_name: variableName,
                variable_type: isStaticVariable(variableName)
                  ? ComposerVariableTypesEnum.DEFAULT
                  : ComposerVariableTypesEnum.CUSTOM,
                canned_answer_id: answer?.id,
              });
            }}
            toolbarBottom={toolbarBottom}
          />
          <StyledSectionedFieldsContainer>
            <SettingsSingleAnswerParentFolderField
              editedFolder={undefined}
              allAvailableNamespaces={[namespace]}
              onFolderSelect={(selectedFolderId) => {
                setFolderId(selectedFolderId);
                setIsDirty(true);
              }}
              selectedFolderId={folderId}
              title={<Trans id="answereditor-filetitle">File template under</Trans>}
              subtitle={
                <Trans id="answereditor-filesubtitle">
                  Organize this message template under an existing folder
                </Trans>
              }
            />
            {isTeamNamespace(namespace) && (
              <SettingsInboxesField
                subtitle={
                  <Trans id="settingstemplates-inboxaccess-subtitle">
                    Anyone can add this template to any conversation
                  </Trans>
                }
                namespace={namespace}
                isVisibleInAllInboxes={showInAllInboxes}
                onChange={(newShowInAllInboxes) => {
                  setIsDirty(true);
                  setShowInAllInboxes(newShowInAllInboxes);
                }}
                inboxes={inboxes}
                onPickerHeaderChange={() => {
                  setIsDirty(true);
                  const isAllSelected = inboxIds.length === inboxes.length;
                  setInboxIds(isAllSelected ? [] : inboxes.map((i) => i.id));
                }}
                onInboxChange={(inbox) => {
                  setIsDirty(true);
                  setInboxIds((prev) => arrayToggle(prev, inbox.id));
                }}
                selectedInboxIds={inboxIds}
              />
            )}
          </StyledSectionedFieldsContainer>
        </StyledSettingsFormContent>
      </StyledSettingsFormMain>
      {maybeRenderFooter()}
    </StyledSettingsWrapper>
  );
};
